import { Box } from "@mui/material";
import { FC } from "react";
import { SalifyImage } from "../../ui/image/image";
import { ImageBlock as ImageBlockType } from "../types";

interface ImageBlockProps {
  image: ImageBlockType;
  theme: string;
}

const ImageBlock: FC<ImageBlockProps> = ({ image, theme }) => {
  const getImageStyles = () => {
    const baseStyles = {
      width: "100%",
      height: "auto",
      borderRadius: "12px",
      objectFit: "cover" as const,
      display: "block",
    };

    // Handle aspect ratio
    if (image.aspectRatio) {
      const [width, height] = image.aspectRatio.split(":").map(Number);
      return {
        ...baseStyles,
        aspectRatio: `${width}/${height}`,
        // Ensure proper sizing for aspect ratio
        height: "auto",
        maxWidth: "100%",
        ...image.style,
      };
    }

    return {
      ...baseStyles,
      // Default to better image sizing when no aspect ratio
      minHeight: "200px",
      maxHeight: "400px",
      ...image.style,
    };
  };

  const getBadgePosition = () => {
    if (!image.badge) return {};

    const position = image.badge.position || "top-left";
    const size = image.badge.size || "80px";

    const positions = {
      "top-left": { top: "16px", left: "16px" },
      "top-right": { top: "16px", right: "16px" },
      "bottom-left": { bottom: "16px", left: "16px" },
      "bottom-right": { bottom: "16px", right: "16px" },
    };

    return {
      position: "absolute" as const,
      ...positions[position],
      width: size,
      height: size,
      zIndex: 2,
      ...image.badge.style,
    };
  };

  const containerStyles = {
    position: "relative" as const,
    overflow: "hidden",
    borderRadius: "12px",
    width: "100%",
    height: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...image.wrapperStyle,
    ...(image.overlay && {
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3))",
        zIndex: 1,
      },
    }),
  };

  return (
    <Box sx={containerStyles}>
      <SalifyImage src={image.src} alt={image.alt || ""} style={getImageStyles()} />
      {image.badge && (
        <SalifyImage
          src={image.badge.src}
          alt={image.badge.alt || "Badge"}
          style={getBadgePosition()}
        />
      )}
    </Box>
  );
};

export default ImageBlock;
